import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, Check, ChevronDown, Clock, Lock, Users, X } from 'lucide-react';
import { DraftShell, NameField, TopicRow, difficultyLabel } from './PracticeSetBox';
import { classDataChanged, fetchRoster, teacherApi } from '../data/api';
import { TITLE_MAX, dueLocalIsPast, dueLocalValue, editPayload, hasEdits, lockedSummary, notOpenYet, studentRows, validateDraft } from '../data/homeworkEdit';
import { topicById, topicsFromLegacy, topicsOf } from '../data/homeworkV4';
import usePrefersReducedMotion from '../../utils/usePrefersReducedMotion';
import { DatePopover } from './DatePicker';
import './practiceSetBox.css';

/* Editing an assigned practice set, in the same box as a new one (Sandy
   2026-10-16: "the same thing on this page if they want to edit the
   assignments"). It opens in place of the set's row on the Assignments page
   and replaces the old Edit pop-up (HomeworkEditPanel), with the same rules:
   the name, due date, close at the due date, the start (only while the set
   is not open yet) and who gets it can change; topics, difficulty and length
   are fixed once assigned (the server's rule). Every change is one
   updateHomework call carrying the set's rev, so a second editor gets STALE
   instead of overwriting. Removing a student erases their answers to this
   set: each removal is confirmed, and happens on Save.
   `initial` (Buffy's editHomework action, EditHomeworkChat): the change the
   teacher asked for, filled in as the starting values ({ name, due, start,
   closeAtDue, showStudents }); it still saves only on Save changes. */

const dayLabel = local => { const t = new Date(local); const day = t.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }); return t.getHours() === 23 && t.getMinutes() === 59 ? day : `${day}, ${t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`; };

// The set's topics as rows. Older sets (v1/v3) list legacy concept ids; they
// map onto today's topics, so their examples open the same way.
function setTopics(hw) {
  const subject = hw.subject || 'reading';
  if (hw.coverage === 'topics' && Array.isArray(hw.topicIds)) return hw.topicIds.map(id => topicById(subject, id)).filter(Boolean);
  if (hw.coverage === 'domain' && hw.domainId) return topicsOf(subject).filter(t => t.domain === hw.domainId);
  if (hw.coverage === 'field' || hw.selectionVersion === 4) return topicsOf(subject);
  const legacy = [hw.questionConceptIds, hw.conceptIds].find(list => Array.isArray(list) && list.length);
  return legacy ? topicsFromLegacy(subject, legacy).map(id => topicById(subject, id)).filter(Boolean) : [];
}

// A date pill: reads as words, opens the house date-and-time panel under it
// on click, x clears it.
function DatePill({ icon: Icon, value, label, emptyLabel, ariaName, onChange, onClear, clearLabel, busy, defaultTime = '23:59' }) {
  const [editing, setEditing] = useState(false);
  const pill = useRef(null);
  const btn = useRef(null);
  return <span ref={pill} className="dc-pill dc-due"><Icon size={15} aria-hidden="true" />
    <button ref={btn} type="button" className="dc-plain" aria-haspopup="dialog" aria-expanded={editing} aria-label={value ? `${label}. Change it` : `${emptyLabel}. Add one`} disabled={busy} onClick={() => setEditing(true)}>{value ? label : emptyLabel}</button>
    {editing && <DatePopover anchor={pill} returnFocus={btn} mode="datetime" label={ariaName} value={value} defaultTime={defaultTime} onChange={onChange} onClose={() => setEditing(false)} />}
    {value && onClear && <button type="button" className="dc-x" aria-label={clearLabel} title={clearLabel} disabled={busy} onClick={() => { setEditing(false); onClear(); }}><X size={13} /></button>}
  </span>;
}

export default function AssignedSetBox({ hw, auth, classId, onClose, onSaved, onReload, onFinish, onDelete, initial = null, fromBuffy = false, focusName = true }) {
  const seed = initial || {};
  const [title, setTitle] = useState(seed.name ?? (hw.title || ''));
  const [dueLocal, setDueLocal] = useState(seed.due ?? dueLocalValue(hw.dueDate));
  // No due date means it can't close at one.
  const [closeAtDue, setCloseAtDue] = useState((seed.due ?? dueLocalValue(hw.dueDate)) ? (seed.closeAtDue ?? !!hw.closeAtDue) : false);
  const scheduled = notOpenYet(hw);
  const [startLocal, setStartLocal] = useState(scheduled ? (seed.start ?? dueLocalValue(hw.startAt)) : '');
  const [add, setAdd] = useState(() => new Set());
  const [remove, setRemove] = useState(() => new Set());
  const [confirmRemove, setConfirmRemove] = useState(null); // { userId, name }
  const [showStudents, setShowStudents] = useState(!!seed.showStudents);
  const [adding, setAdding] = useState(false);
  const [roster, setRoster] = useState(null);
  const [rosterFailed, setRosterFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [stale, setStale] = useState(false);
  const [confirmPast, setConfirmPast] = useState(false);
  const [open, setOpen] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const writing = useRef(false);
  const box = useRef(null);
  const toggles = useRef({});
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    let alive = true;
    fetchRoster(auth, classId)
      .then(list => { if (alive) setRoster([...(list || [])].sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }))); })
      .catch(() => { if (alive) setRosterFailed(true); });
    return () => { alive = false; };
  }, [auth, classId]);
  // Opens in place of the row: bring it into view and put focus on the name.
  useEffect(() => {
    const el = box.current;
    el?.scrollIntoView?.({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
    // In the chat (focusName false) the teacher's typing stays in the input box.
    if (focusName) el?.querySelector('textarea')?.focus({ preventScroll: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => (roster ? studentRows(hw, roster) : null), [hw, roster]);
  const locked = lockedSummary(hw);
  const topics = useMemo(() => setTopics(hw), [hw]);
  const draft = { title, dueLocal, closeAtDue, add, remove, ...(scheduled ? { startLocal } : {}) };
  const payload = editPayload(hw, draft);
  const changed = hasEdits(payload);
  const problem = validateDraft(hw, draft, rows);
  const pastDue = dueLocalIsPast(dueLocal);
  const dueChanged = 'dueDate' in payload;
  const range = hw.levelMode === 'range' && hw.levelMin != null && hw.levelMax != null ? { min: hw.levelMin, max: hw.levelMax } : null;
  const hard = range ? difficultyLabel('range', range) : locked.levels === 'Levels you picked' ? 'Levels you picked' : difficultyLabel('student');
  const collapsed = hw.coverage === 'field' && !showAll;
  const cover = hw.coverage === 'domain' ? `All of ${locked.coverage}` : hw.coverage === 'field' ? `A mix of every ${hw.subject === 'both' ? 'SAT' : locked.subject} topic` : !topics.length ? locked.coverage : '';

  const inSet = rows ? rows.current.filter(s => (s.removed ? add.has(s.userId) : !remove.has(s.userId))).length + (rows.picked ? add.size : 0) : null;
  const whoLabel = !rows ? (Array.isArray(hw.studentIds) && hw.studentIds.length ? `${new Set(hw.studentIds).size} students` : 'Whole class')
    : rows.picked ? `${inSet} ${inSet === 1 ? 'student' : 'students'}` : `Whole class${rows.current.length ? ` · ${inSet} of ${rows.current.length}` : ''}`;

  const toggleAdd = userId => setAdd(s => { const n = new Set(s); if (n.has(userId)) n.delete(userId); else n.add(userId); return n; });
  const confirmRemoval = () => { if (!confirmRemove) return; setRemove(s => new Set(s).add(confirmRemove.userId)); setConfirmRemove(null); };
  const undoRemove = userId => setRemove(s => { const n = new Set(s); n.delete(userId); return n; });

  const save = async ({ pastConfirmed = false } = {}) => {
    if (writing.current || busy || stale) return;
    if (problem) { setError(problem); return; }
    if (!changed) { onClose(); return; }
    if (dueChanged && pastDue && !pastConfirmed) { setConfirmPast(true); return; }
    setConfirmPast(false);
    writing.current = true; setBusy(true); setError('');
    try {
      const r = await teacherApi(auth, 'updateHomework', { classID: classId, ...payload });
      classDataChanged(classId);
      onSaved(r);
    } catch (err) {
      if (err.status === 409 && err.data?.code === 'STALE') setStale(true);
      else if (err.status >= 400 && err.status < 500 && err.status !== 408) setError(err.message || 'Couldn’t save these changes.');
      else setError('Couldn’t save these changes. Check your connection and try again.');
    } finally { writing.current = false; setBusy(false); }
  };

  const message = stale ? 'Someone else changed this assignment. Reload to see the latest.'
    : confirmPast ? `This due date has already passed${closeAtDue ? ', so the set closes as soon as you save' : ''}. Save anyway?`
      : error || (changed && problem) || (changed ? (fromBuffy ? 'Buffy filled in the change. Nothing changes for students until you save.' : 'Nothing changes for students until you save.') : 'No changes yet.');
  const alert = stale || error || (changed && problem);

  return <div ref={box} className="asb" onKeyDown={e => { if (e.key === 'Escape' && !busy && !confirmRemove) onClose(); }}>
    <DraftShell label="Assigned practice set" tag="Assigned" busy={busy} className="psb asb-tile">
      <div className="dc-body">
        <NameField className="bc-title dc-title" aria-label="Assignment name" value={title} maxLength={TITLE_MAX} disabled={busy} onChange={setTitle} />

        <section className="psb-topics" aria-label="What students practice">
          {cover && <p className="psb-cover">{cover}</p>}
          {topics.length > 0 && (collapsed
            ? <button type="button" className="psb-all" onClick={() => setShowAll(true)}>See the {topics.length} topics<ChevronDown size={15} aria-hidden="true" /></button>
            : <ul className="psb-list">{topics.map(t => <TopicRow key={t.id} subject={hw.subject || 'reading'} topic={t} range={range} busy={busy}
              open={open === t.id} onToggle={() => setOpen(open === t.id ? null : t.id)} onHide={() => { setOpen(null); toggles.current[t.id]?.focus(); }}
              toggleRef={el => { toggles.current[t.id] = el; }} />)}</ul>)}
          {hw.coverage === 'field' && showAll && <button type="button" className="psb-all psb-all-less" onClick={() => { setShowAll(false); setOpen(null); }}>Hide the topic list</button>}
        </section>

        <section className="psb-levels asb-fixed" aria-label="Question levels">
          <div className="psb-how"><span>How hard: <span className="psb-how-value">{hard}</span></span></div>
          <p className="asb-lock"><Lock size={13} aria-hidden="true" />Topics, difficulty and length stay the same once assigned. Make a new set to change them.</p>
        </section>

        <div className="dc-pills">
          <span className="dc-pill dc-static">{locked.count} questions</span>
          {scheduled && <DatePill icon={Clock} value={startLocal} label={`Opens ${startLocal ? dayLabel(startLocal) : ''}`} emptyLabel="Opens when you save" ariaName="Students can start" defaultTime="07:00"
            onChange={setStartLocal} onClear={() => setStartLocal('')} clearLabel="Open it now" busy={busy} />}
          <DatePill icon={CalendarDays} value={dueLocal} label={`Due ${dueLocal ? dayLabel(dueLocal) : ''}`} emptyLabel="No due date" ariaName="Due date"
            onChange={v => { setDueLocal(v); setConfirmPast(false); }} onClear={() => { setDueLocal(''); setCloseAtDue(false); setConfirmPast(false); }} clearLabel="Remove the due date" busy={busy} />
          {dueLocal && <button type="button" className="dc-pill asb-close" aria-pressed={closeAtDue} disabled={busy} onClick={() => setCloseAtDue(v => !v)}>
            {closeAtDue ? <Check size={14} aria-hidden="true" /> : <span className="asb-box" aria-hidden="true" />}Close at the due date</button>}
          <button type="button" className="dc-pill asb-who" aria-expanded={showStudents} disabled={busy} onClick={() => setShowStudents(v => !v)}>
            <Users size={15} aria-hidden="true" />{whoLabel}<ChevronDown size={14} aria-hidden="true" /></button>
        </div>
        {dueLocal && pastDue && <p className="psb-note asb-warn">{closeAtDue ? 'This date has passed, so the set is closed. Students can only review their answers.' : 'This date has passed. Students see the set as overdue.'}</p>}

        {showStudents && <section className="asb-students" aria-label="Students on this assignment">
          {rosterFailed && <p className="psb-error">Couldn’t load the class list. You can still change the name and dates.</p>}
          {!rosterFailed && !rows && <p className="psb-note" role="status">Loading students…</p>}
          {rows && <>
            <p className="psb-note">Removing a student erases their answers to this set.</p>
            <ul className="asb-list">
              {rows.current.map(s => {
                const pendingRemove = remove.has(s.userId);
                const pendingAdd = add.has(s.userId);
                const out = s.removed ? !pendingAdd : pendingRemove;
                const asking = confirmRemove?.userId === s.userId;
                return <li key={s.userId}>
                  <div className="asb-student">
                    <span className={`asb-name${out ? ' is-out' : ''}`}>{s.name}
                      {pendingRemove && <span className="asb-sub">Removed when you save.</span>}
                      {s.removed && !pendingAdd && <span className="asb-sub">Not on this set.</span>}
                      {s.removed && pendingAdd && <span className="asb-sub">Added back when you save.</span>}
                      {!s.enrolled && <span className="asb-sub">No longer in this class.</span>}</span>
                    {s.removed ? <button type="button" className="bc-text" disabled={busy} onClick={() => toggleAdd(s.userId)}>{pendingAdd ? 'Undo' : 'Add back'}</button>
                      : pendingRemove ? <button type="button" className="bc-text" disabled={busy} onClick={() => undoRemove(s.userId)}>Undo</button>
                        : <button type="button" className="bc-text" aria-label={`Remove ${s.name}`} disabled={busy || asking} onClick={() => setConfirmRemove({ userId: s.userId, name: s.name })}>Remove</button>}
                  </div>
                  {asking && <div className="asb-confirm" role="alertdialog" aria-label={`Remove ${s.name}?`}>
                    <p>Remove {s.name}? Their answers to this set are erased when you save.</p>
                    <div><button type="button" className="bc-btn" onClick={() => setConfirmRemove(null)}>Keep them</button><button type="button" className="bc-btn asb-danger" onClick={confirmRemoval}>Remove</button></div>
                  </div>}
                </li>;
              })}
              {!rows.current.length && <li className="asb-student psb-note">No students are in this class yet.</li>}
            </ul>
            {rows.picked && rows.addable.length > 0 && (!adding ? <button type="button" className="bc-text asb-add" disabled={busy} onClick={() => setAdding(true)}>Add students</button>
              : <ul className="asb-list asb-addable" aria-label="Students to add">
                {rows.addable.map(s => <li key={s.userId} className="asb-student"><span className="asb-name">{s.name}{add.has(s.userId) && <span className="asb-sub">Added when you save.</span>}</span>
                  <button type="button" className="bc-text" aria-label={`${add.has(s.userId) ? 'Undo adding' : 'Add'} ${s.name}`} disabled={busy} onClick={() => toggleAdd(s.userId)}>{add.has(s.userId) ? 'Undo' : 'Add'}</button></li>)}
              </ul>)}
          </>}
        </section>}

        {(onFinish || onDelete) && <p className="psb-note asb-end">Done with it?{onFinish && <> <button type="button" className="bc-text" disabled={busy} onClick={onFinish}>Finish it</button></>}{onFinish && onDelete && ' or'}{onDelete && <> <button type="button" className="bc-text" disabled={busy} onClick={onDelete}>delete it</button></>}.</p>}
      </div>
      <div className="bc-foot">
        <p className={alert ? 'asb-alert' : undefined} role={alert ? 'alert' : undefined}>{message}</p>
        <div className="bc-actions">
          {stale ? <button type="button" className="bc-btn bc-btn-go" onClick={onReload}>Reload assignments</button>
            : confirmPast ? <><button type="button" className="bc-btn" disabled={busy} onClick={() => setConfirmPast(false)}>Go back</button>
              <button type="button" className="bc-btn bc-btn-go" disabled={busy} onClick={() => save({ pastConfirmed: true })}>{busy ? 'Saving…' : 'Save anyway'}</button></>
              : <><button type="button" className="bc-btn" disabled={busy} onClick={onClose}>Cancel</button>
                <button type="button" className="bc-btn bc-btn-go" disabled={busy || !changed || !!problem} onClick={() => save()}>{busy ? 'Saving…' : 'Save changes'}</button></>}
        </div>
      </div>
    </DraftShell>
  </div>;
}
